/**
 * iter98 / Session E — Employee Admin "Check Orders" page.
 *
 * Restricted read-only order list for role='employee_admin' with a
 * narrow forward-only fulfillment-status control.  The backend at
 * ``/api/employee/orders`` already scrubs return/refund/internal-notes
 * fields BEFORE returning the payload, so this page never sees them.
 *
 * Everything BELOW the "Update Status (Fulfillment)" section from the
 * super-admin Orders view is intentionally absent here: no Return &
 * Refund Management, no Danger Zone, no delete button, no internal
 * notes editor, no Shiprocket weight preview / retry / dimensions.
 */
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

// Forward-only progression the employee is permitted to set.  Kept in
// sync with the backend ``_EMPLOYEE_FULFILLMENT_ALLOWED`` set.
const NEXT_ALLOWED = {
  placed: ["confirmed"],
  confirmed: ["packed"],
  packed: ["shipped"],
  shipped: ["delivered"],
  delivered: [],
  cancelled: [],
  returned: [],
  payment_pending: ["confirmed"],
};

const fmtCurrency = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })
    .format(Number(n) || 0);

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata",
    });
  } catch { return iso; }
};

export default function EmployeeOrders() {
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !["employee_admin", "super_admin", "admin"].includes(user?.role)) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/employee/orders");
        if (!cancelled) setOrders(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, user]);

  if (authLoading) return null;
  // iter102 / Session H — an unauthenticated visitor to /admin-orders is
  // silently sent home rather than to the employee-admin login page.
  // We do NOT reveal the new obscured employee-admin login URL to
  // anyone who lands here without an active session.
  if (!user) return <Navigate to="/" replace />;
  // Any role OTHER than employee/super admin is denied.
  if (!["employee_admin", "super_admin", "admin"].includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  const advance = async (order, next) => {
    setUpdatingId(order.order_id);
    try {
      const { data } = await api.put(
        `/employee/orders/${order.order_id}/fulfillment-status`,
        null,
        { params: { status: next } },
      );
      setOrders((prev) => prev.map((o) => (o.order_id === order.order_id ? { ...o, ...data } : o)));
      toast.success(`Order ${order.order_id.slice(-6)} → ${next}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-10" data-testid="employee-orders-page">
      <header className="mb-6">
        <h1 className="font-serif text-3xl text-brand-dark">Check Orders</h1>
      </header>

      {loading ? (
        <div className="text-sm text-brand-muted">Loading orders…</div>
      ) : orders.length === 0 ? (
        <div className="text-sm text-brand-muted">No orders yet.</div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => {
            const addr = o.shipping_address || o.address || {};
            const nextOptions = NEXT_ALLOWED[o.order_status] || [];
            return (
              <div
                key={o.order_id}
                className="bg-white border border-brand-line rounded-2xl p-5"
                data-testid={`employee-order-${o.order_id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-xs text-brand-muted">Order ID</div>
                    <div className="font-mono text-sm text-brand-dark" data-testid={`emp-order-id-${o.order_id}`}>
                      {o.order_id}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-brand-muted">Customer</div>
                    <div className="text-sm text-brand-dark">
                      {o.user_name || addr.full_name || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-brand-muted">Placed on</div>
                    <div className="text-sm text-brand-dark">{fmtDateTime(o.created_at)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-brand-muted">Total</div>
                    <div className="text-sm text-brand-dark font-medium">{fmtCurrency(o.total)}</div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <div className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-1">Items</div>
                    <ul className="text-sm text-brand-dark space-y-1">
                      {(o.items || []).map((it, i) => (
                        <li key={i}>
                          {it.name}{it.variant_label ? ` (${it.variant_label})` : ""} × {it.quantity}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <div className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-1">Customer address</div>
                    <div className="text-sm text-brand-dark">
                      {addr.full_name}<br />
                      {[addr.house_no, addr.street, addr.landmark].filter(Boolean).join(", ")}<br />
                      {addr.city}, {addr.state} — {addr.pincode}<br />
                      Phone: {addr.phone}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-1">Shipping</div>
                    <div className="text-sm text-brand-dark">
                      Courier: {o.courier_name || "—"}<br />
                      AWB: <span className="font-mono">{o.awb_code || "—"}</span><br />
                      Tracking:{" "}
                      {o.tracking_url ? (
                        <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-brand-chili">
                          Open
                        </a>
                      ) : (
                        "—"
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-1">Payment</div>
                    <div className="text-sm text-brand-dark">
                      Method: <span className="uppercase">{o.payment_method || "—"}</span><br />
                      Status: <span className="capitalize">{o.payment_status || "—"}</span>
                      {o.razorpay_payment_id && (
                        <><br />Ref: <span className="font-mono text-xs">{o.razorpay_payment_id}</span></>
                      )}
                    </div>
                  </div>
                </div>

                {/* Update status (fulfillment) — narrow forward-only. */}
                <div className="mt-4 pt-4 border-t border-brand-line">
                  <div className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
                    Update Status (Fulfillment)
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm">
                      Current: <b className="capitalize" data-testid={`emp-status-${o.order_id}`}>
                        {o.order_status}
                      </b>
                    </span>
                    {nextOptions.length === 0 ? (
                      <span className="text-xs text-brand-muted italic ml-2">
                        {["delivered", "cancelled", "returned"].includes(o.order_status)
                          ? "Terminal — Super Admin only can act further."
                          : "No further employee action available."}
                      </span>
                    ) : (
                      nextOptions.map((next) => (
                        <button
                          key={next}
                          type="button"
                          disabled={updatingId === o.order_id}
                          onClick={() => advance(o, next)}
                          className="text-xs px-3 py-1.5 border border-brand-chili text-brand-chili rounded-full hover:bg-brand-chili hover:text-white transition-colors disabled:opacity-60"
                          data-testid={`emp-advance-${o.order_id}-${next}`}
                        >
                          Mark as {next}
                        </button>
                      ))
                    )}
                  </div>
                </div>
                {/* NOTE: Return & Refund Management, internal admin notes,
                    delete controls, danger zone, Shiprocket internal
                    payload views etc. are INTENTIONALLY absent here. */}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
